/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Zap,
	VolumeX,
	BatteryCharging,
	Usb,
	Smartphone,
	Wifi,
	SlidersHorizontal, // Use SlidersHorizontal for control
	CheckCircle,
	ArrowRight,
	LifeBuoy,
	RadioTower, // Use RadioTower for Hub
} from "lucide-react"; // Refined icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// --- Structured Data for Motorization Features & Benefits ---
const motorizationData = {
	hero: {
		title: "Effortless Control, Smart Living",
		subtitle: "Upgrade Your Home with Automated Motorized Shades",
		text: "Imagine adjusting all your window shades with a single touch, a voice command, or even automatically based on your schedule. Our premium motorized solutions bring convenience, safety, and modern luxury to your home.",
		image: "/images/info/automation.png", // Use a lifestyle image showing remote/app use
	},
	keyBenefits: [
		{
			icon: Zap,
			title: "Ultimate Convenience",
			text: "Adjust hard-to-reach windows or multiple shades instantly via remote, app, or voice.",
		},
		{
			icon: CheckCircle,
			title: "Enhanced Child & Pet Safety",
			text: "Eliminate dangling cords for a safer environment for your loved ones.",
		},
		{
			icon: Smartphone,
			title: "Smart Home Ready",
			text: "Integrate seamlessly with popular systems like Alexa, Google Home, and more via our Connector Hub & app.",
		},
		{
			icon: BatteryCharging,
			title: "Easy Maintenance",
			text: "Long-lasting rechargeable batteries (~6 months) with simple USB-C charging.",
		},
		{
			icon: VolumeX,
			title: "Whisper Quiet",
			text: "Enjoy smooth, ultra-quiet operation (around 32 dB).",
		},
		// { icon: Sun, title: "Energy Efficiency", text: "Schedule shades to open/close automatically to help manage heat gain/loss." }, // Optional benefit
	],
	howItWorks: {
		title: "Simple & Powerful Technology",
		components: [
			{
				icon: RadioTower, // Changed from Wifi
				title: "The Connector+ Hub (Optional)",
				text: "Unlock the full potential of smart home integration. This small hub connects your motorized shades to your Wi-Fi network, enabling control via the Smart Life app and voice assistants.",
				image: "/images/info/connector.png", // Hub image
			},
			{
				icon: Smartphone,
				title: "The Smart Life App",
				text: "Control your shades individually or in groups from anywhere. Set custom schedules (like waking up to natural light), create scenes, and link with Alexa or Google Home for voice commands.",
				image: "/images/info/smartlife.png", // App screenshot/mockup
				links: [
					// Add app store links
					{
						href: "https://play.google.com/store/apps/details?id=com.tuya.smartlife",
						text: "Google Play",
					},
					{
						href: "https://apps.apple.com/us/app/smart-life-smart-living/id1115101477",
						text: "App Store",
					},
				],
			},
			{
				icon: SlidersHorizontal, // Changed from Wifi
				title: "Multi-Channel Remotes",
				text: "Prefer a dedicated remote? Our stylish remotes control multiple shades individually or as groups (up to 16 channels + custom groups). Perfect for quick adjustments without your phone.",
				image: "/images/info/remote.png", // Remote image
			},
			{
				icon: BatteryCharging, // Use BatteryCharging icon
				title: "Rechargeable Motors",
				text: "Our powerful yet quiet motors feature built-in lithium-ion batteries. A single charge using a standard USB-C cable lasts approximately 6 months with typical use.",
				image: "/images/info/batterypower.png", // Image showing motor/charging port
			},
		],
	},
	compatibility: {
		title: "Smart Home Integration",
		text: "Connect easily with your favorite smart home ecosystems through the Connector+ Hub and Smart Life app.",
		image: "/images/info/smart.jpg", // Smart home logos
	},
	howToOrder: {
		title: "Adding Motorization is Easy",
		steps: [
			"Choose your desired shade style (Roller, Zebra, etc.) on the Shop page.",
			"Enter your dimensions and select fabric on the Customizer.",
			"In the 'Control' step, simply select the 'Automated (Motorized)' option.",
			"You'll then be prompted to add optional accessories like remotes, chargers, or the Connector+ Hub.",
			"Complete the remaining steps and add to your cart!",
		],
		cta: { text: "Start Customizing Now", link: "/shop" },
	},
};

// --- Reusable Feature/Benefit Card ---
const FeatureCard = ({ icon: Icon, title, description }) => (
	<div className="flex items-start gap-4 p-4">
		<div className="mt-1 flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
			{Icon && <Icon size={24} strokeWidth={1.5} />}
		</div>
		<div>
			<h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
			<p className="text-sm text-gray-600">{description}</p>
		</div>
	</div>
);

// --- Main Motorized/Smart Page Component ---
const MotorizedSmart = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			{" "}
			{/* Standard BG */}
			<Helmet>
				<title>Motorized & Smart Shades | Automation | Off Cut Shades</title>{" "}
				{/* SEO Title */}
				<meta
					name="description"
					content="Discover the convenience and luxury of automated motorized window shades from Off Cut Shades. Control via remote, app, or voice with smart home integration."
				/>{" "}
				{/* SEO Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/motorized-solutions"
				/>
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 mb-16 md:mb-20">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
						{/* Text Content */}
						<motion.div
							initial={{ opacity: 0, x: -30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, ease: "easeOut" }}
							className="text-center lg:text-left"
						>
							<p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">
								{motorizationData.hero.subtitle}
							</p>
							<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
								{motorizationData.hero.title}
							</h1>
							<p className="text-lg text-gray-600 max-w-xl mx-auto lg:mx-0 mb-8">
								{motorizationData.hero.text}
							</p>
							<Link
								to="/shop?feature=Motorized"
								className="btn btn-primary text-base px-7 py-3"
							>
								{" "}
								{/* Link to pre-filtered shop */}
								Explore Motorized Shades <ArrowRight size={18} />
							</Link>
						</motion.div>
						{/* Image */}
						<motion.div
							initial={{ opacity: 0, x: 30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
							className="relative aspect-video lg:aspect-square rounded-xl shadow-xl overflow-hidden"
						>
							<img
								src={motorizationData.hero.image}
								alt="Woman controlling motorized shades with remote"
								className="w-full h-full object-cover"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
						</motion.div>
					</div>
				</section>
				{/* --- Key Benefits Section --- */}
				<section className="py-16 md:py-20 bg-gradient-to-b from-white via-gray-50 to-gray-50">
					{" "}
					{/* Subtle bg change */}
					<div className="container mx-auto px-4">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5 }}
							className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10 md:mb-14"
						>
							Why Choose Motorized Shades?
						</motion.h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
							{motorizationData.keyBenefits.map((benefit, index) => (
								<motion.div
									key={benefit.title}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true, amount: 0.3 }}
									transition={{ duration: 0.5, delay: index * 0.1 }}
								>
									<FeatureCard
										icon={benefit.icon}
										title={benefit.title}
										description={benefit.text}
									/>
								</motion.div>
							))}
						</div>
					</div>
				</section>
				{/* --- How It Works Section --- */}
				<section className="py-16 md:py-20 bg-white">
					<div className="container mx-auto px-4">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5 }}
							className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10 md:mb-14"
						>
							{motorizationData.howItWorks.title}
						</motion.h2>
						{/* Grid for components */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-center max-w-5xl mx-auto">
							{motorizationData.howItWorks.components.map(
								(component, index) => (
									<motion.div
										key={component.title}
										className={`space-y-4 ${
											index % 2 !== 0 ? "md:order-first" : ""
										}`} // Alternate order
										initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
										whileInView={{ opacity: 1, x: 0 }}
										viewport={{ once: true, amount: 0.3 }}
										transition={{ duration: 0.6, delay: 0.1 }}
									>
										<div className="flex items-center gap-3 mb-3">
											<div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
												<component.icon size={24} strokeWidth={1.5} />
											</div>
											<h3 className="text-xl font-semibold text-gray-800">
												{component.title}
											</h3>
										</div>
										<p className="text-gray-600 text-sm md:text-base">
											{component.text}
										</p>
										{/* App store links for Smart Life */}
										{component.links && (
											<div className="flex items-center gap-4 pt-2">
												<span className="text-xs text-gray-500">
													Get the App:
												</span>
												{component.links.map((link) => (
													<a
														key={link.text}
														href={link.href}
														target="_blank"
														rel="noopener noreferrer"
														className="text-xs font-medium text-primary hover:underline"
													>
														{link.text}
													</a>
												))}
											</div>
										)}
										{component.image && (
											<img
												src={component.image}
												alt={component.title}
												className="mt-4 rounded-lg shadow-md border border-gray-100 w-full max-w-xs mx-auto md:mx-0"
											/>
										)}
									</motion.div>
								)
							)}
						</div>
					</div>
				</section>
				{/* --- Smart Home Compatibility --- */}
				<section className="py-16 md:py-20 bg-gray-50">
					<div className="container mx-auto px-4 text-center">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5 }}
							className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
						>
							{motorizationData.compatibility.title}
						</motion.h2>
						<motion.p
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="text-gray-600 max-w-2xl mx-auto mb-8"
						>
							{motorizationData.compatibility.text}
						</motion.p>
						<motion.img
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							src={motorizationData.compatibility.image}
							alt="Compatible Smart Home Platforms (Alexa, Google Home, etc.)"
							className="rounded-lg max-w-3xl w-full mx-auto"
							loading="lazy"
						/>
					</div>
				</section>
				{/* --- How to Order Section --- */}
				<section className="py-16 md:py-20 bg-white">
					<div className="container mx-auto px-4 max-w-3xl mx-auto text-center">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5 }}
							className="text-3xl md:text-4xl font-bold text-gray-900 mb-8"
						>
							{motorizationData.howToOrder.title}
						</motion.h2>
						{/* Step-by-step list */}
						<ol className="text-left space-y-4 max-w-lg mx-auto text-gray-700 mb-8">
							{motorizationData.howToOrder.steps.map((step, index) => (
								<motion.li
									key={index}
									className="flex items-start gap-3"
									initial={{ opacity: 0, x: -20 }}
									whileInView={{ opacity: 1, x: 0 }}
									viewport={{ once: true, amount: 0.5 }}
									transition={{ duration: 0.4, delay: index * 0.1 }}
								>
									<div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold mt-0.5">
										{index + 1}
									</div>
									<span>{step}</span>
								</motion.li>
							))}
						</ol>
						{/* CTA Button */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.3 }}
						>
							<Link
								to={motorizationData.howToOrder.cta.link}
								className="btn btn-primary px-7 py-3 text-base"
							>
								{motorizationData.howToOrder.cta.text} <ArrowRight size={18} />
							</Link>
						</motion.div>
					</div>
				</section>
			</main>
			{/* --- Contact Block --- */}
			<ContactBlock
				title="Questions About Motorization?"
				description="Our specialists can help you understand the options, compatibility, and benefits for your specific needs."
			/>
		</div>
	);
};

export default MotorizedSmart;
